// @ts-ignore
import QueryParams from '@waze/george/lib/query-params'
import { OS, identifyOS } from './browser'

const PLAYSTORE_BASE_URL = 'https://play.google.com/store/apps/details'

// See https://itunespartner.apple.com/en/apps/faq/App%20Analytics_Campaigns)
const APPSTORE_PROVIDER_TOKEN = '274039'
const APPSTORE_MEDIA_TYPE = 8 // Mobile Software Applications
const APPSTORE_BASE_URL = 'https://itunes.apple.com/app/app-store'

export interface IApp {
  playStoreId: string
  appStoreId: string
  universalLink: string
  firebaseBundleId: string
  firebaseUrlScheme: string
  firebaseUrl: string
}

export const AppWaze: IApp = {
  playStoreId: 'com.waze',
  appStoreId: '323229106',
  universalLink: 'https://www.waze.com/ul',
  firebaseBundleId: 'com.waze.iphone',
  firebaseUrlScheme: 'waze',
  firebaseUrl: 'https://mtjz3.app.goo.gl'
}

export const AppRider: IApp = {
  playStoreId: 'com.ridewith',
  appStoreId: '1091029104',
  universalLink: 'https://www.waze.com/rul',
  firebaseBundleId: 'com.waze.rider',
  firebaseUrlScheme: 'wazerider',
  firebaseUrl: 'https://fmww9.app.goo.gl'
}

export interface IUtmParams {
  utm_source: string
  utm_medium?: string
  utm_campaign: string
}

export interface IParams extends IUtmParams {
  app: IApp
}

export interface IMapParams {
  lat: number
  lng: number
  zoom: number
}

const DEFAULT_PARAMS: IParams = {
  app: AppWaze,
  utm_source: QueryParams.get('utm_source') || 'waze_website',
  utm_campaign: QueryParams.get('utm_campaign') || 'default'
}

export function buildDownloadLink(options: Partial<IParams>): string | undefined {
  const params = { ...DEFAULT_PARAMS, ...options }

  const os = identifyOS()

  if (os === OS.ios) {
    return buildAppStoreLink(params)
  } else if (os === OS.android) {
    return buildPlayStoreLink(params)
  }

  return undefined
}

export function buildAppStoreLink(options: Partial<IParams>): string {
  const params = { ...DEFAULT_PARAMS, ...options }
  const os = identifyOS()

  if (os === OS.other) {
    return buildAppStoreDirectLink(params)
  } else {
    return buildAppStoreFirebaseLink(params)
  }
}

export function buildDeeplinkLink(app: IApp): string {
  // without path, the facebook internal browser on iOS ignores the click
  const path = app === AppRider ? 'deeplink' : ''
  return `${app.firebaseUrlScheme}://${path}${window.location.search}`
}

export function buildUniversalLink(params: IParams) {
  return `${params.app.universalLink}?${serializeReferrer(params)}`
}

function buildAppStoreFirebaseLink(params: IParams): string {
  const queryString = QueryParams.stringify({
    link: buildAppStoreUniversalLink(params),
    isi: params.app.appStoreId,
    ibi: params.app.firebaseBundleId,
    ius: params.app.firebaseUrlScheme,
    ct: campaignToken(params),
    pt: APPSTORE_PROVIDER_TOKEN,
    mt: APPSTORE_MEDIA_TYPE,
    efr: 1
  })

  return `${params.app.firebaseUrl}/?${queryString}`
}

function buildAppStoreDirectLink(params: IParams): string {
  const queryString = QueryParams.stringify({
    ct: campaignToken(params),
    pt: APPSTORE_PROVIDER_TOKEN,
    mt: APPSTORE_MEDIA_TYPE
  })

  return `${APPSTORE_BASE_URL}/id${params.app.appStoreId}?${queryString}`
}

function campaignToken(params: IParams): string {
  return [params.utm_source, params.utm_medium, params.utm_campaign].filter(utm => !!utm).join('~')
}

function buildAppStoreUniversalLink(params: IParams): string {
  const queryString = QueryParams.stringify({ ref: serializeReferrer(params), url_ver: '2' })
  return `${params.app.universalLink}?${queryString}`
}

export function buildPlayStoreLink(options: Partial<IParams>): string {
  const params = { ...DEFAULT_PARAMS, ...options }
  const queryString = QueryParams.stringify({
    id: params.app.playStoreId,
    referrer: serializeReferrer(params)
  })
  return `${PLAYSTORE_BASE_URL}?${queryString}`
}

export function serializeReferrer(params: IUtmParams): string {
  const keys: Array<keyof IUtmParams> = ['utm_source', 'utm_medium', 'utm_campaign']
  const query = keys.reduce<{ [key: string]: string }>((result, key) => {
    const value = params[key]
    if (value) {
      result[key] = value
    }
    return result
  }, {})
  return QueryParams.stringify(query)
}

export function buildMapDeeplink(options: Partial<IParams>, mapParams: IMapParams): string {
  const params = { ...DEFAULT_PARAMS, ...options }
  const queryString = QueryParams.stringify({
    ll: `${mapParams.lat.toFixed(8)},${mapParams.lng.toFixed(8)}`,
    referrer: serializeReferrer(params),
    navigate: 'yes',
    zoom: mapParams.zoom
  })

  return `${params.app.universalLink}?${queryString}`
}
